import React, { useReducer, useContext, useCallback, useEffect } from "react";

import reducer from "./reducer";
import getUserDetails from "api/getUserDetails";
import getPortalLicences from "api/getPortalLicences";

import initialState from "./initialState";
import * as actionTypes from "./actionTypes";
import { useLDClient } from "launchdarkly-react-client-sdk";

const AdminContext = React.createContext(initialState);

const AdminProvider = ({ children }) => {
  const ldClient = useLDClient();
  const [state, dispatch] = useReducer(reducer, initialState, (initialState) => ({
    ...initialState,
  }));

  const setToken = useCallback((token) => {
    dispatch({
      type: actionTypes.SET_AUTH_TOKEN,
      payload: {
        token,
      },
    });
  }, []);

  const setUser = useCallback(
    (user) =>
      dispatch({
        type: actionTypes.SET_USER,
        payload: {
          user,
        },
      }),
    []
  );

  const setPortal = useCallback((portal) => {
    dispatch({
      type: actionTypes.SET_PORTAL,
      payload: {
        portal,
      },
    });
  }, []);

  const setOpenUsers = useCallback((openUsers) => {
    dispatch({
      type: actionTypes.SET_OPEN_USERS,
      payload: {
        openUsers,
      },
    });
  }, []);

  const closeUser = useCallback((userId) => {
    dispatch({
      type: actionTypes.CLOSE_USER,
      payload: { userId },
    });
  }, []);

  const openUser = useCallback(
    async ({ portalId, userId }) => {
      try {
        const { data } = await getUserDetails(portalId)(userId);
        const { firstName, lastName } = data;
        const name = `${firstName} ${lastName}` || "User";
        dispatch({
          type: actionTypes.OPEN_USER,
          payload: { user: { userId, name } },
        });
      } catch (e) {
        closeUser(userId);
        throw e;
      }
    },
    [closeUser]
  );

  const setOpenLicences = useCallback((openLicences) => {
    dispatch({
      type: actionTypes.SET_OPEN_LICENCES,
      payload: {
        openLicences,
      },
    });
  }, []);

  const openLicence = useCallback(async ({ portalId, licenceId: targetLicenceId }) => {
    const { data } = await getPortalLicences({ portalId });
    const licence = data
      .filter(({ licence }) => !!licence)
      .find(({ licence: { licenceId } }) => licenceId === targetLicenceId);
    if (licence) {
      const { name = "Unknown Licence" } = licence;
      dispatch({
        type: actionTypes.OPEN_LICENSE,
        payload: { licence: { licenceId: targetLicenceId, name } },
      });
    }
  }, []);

  const setRouteMessage = useCallback(({ pathname: route, message }) => {
    dispatch({
      type: actionTypes.SET_ROUTE_MESSAGE,
      payload: { route, message },
    });
  }, []);

  const deleteRouteMessage = useCallback((route) => {
    dispatch({
      type: actionTypes.DEL_ROUTE_MESSAGE,
      payload: { route },
    });
  }, []);

  const { token, portal = {}, user = {}, openUsers, openLicences, routeMessages } = state;

  const { globalAdmin = false, enrolmentAdmin = false, instanceAdmin = false } = user ?? {};

  const { licensed = false, pcat = false } = portal ?? {};

  const getRouteMessage = (pathname) => routeMessages.find(({ route }) => route === pathname);

  useEffect(() => {
    if (user) {
      ldClient?.identify({
        anonymous: false,
        key: user.username,
        globalAdmin,
        instanceAdmin,
      });
    }
  }, [globalAdmin, instanceAdmin, ldClient, user]);

  const contextValue = {
    state,
    dispatch,

    token,
    portal,
    licensed,
    pcat,
    user,
    globalAdmin,
    enrolmentAdmin,
    instanceAdmin,
    getRouteMessage,

    // tab management
    openUsers,
    setOpenUsers,
    openUser,
    closeUser,

    openLicences,
    setOpenLicences,
    openLicence,

    // setters
    setPortal,
    setUser,
    setToken,
    setRouteMessage,
    deleteRouteMessage,
  };

  return <AdminContext.Provider value={contextValue}>{children}</AdminContext.Provider>;
};

const useAdmin = () => useContext(AdminContext);

export { AdminProvider, useAdmin };
